import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import VehicleServiceHero from '../../../../components/ui/VehicleServiceHero';
import { CheckCircle, Wrench, Shield, Clock, DollarSign, Award, Car, Settings, AlertTriangle, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';

const JaguarService = () => {
  const jaguarFeatures = [
    'British Luxury Experts',
    'SDD/Pathfinder Diagnostics',
    'expert technicians',
    'Genuine JLR Parts',
    '12-Month Warranty',
    'All Jaguar Models'
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const models = [
    { name: 'XE', type: 'Compact Luxury Sedan' },
    { name: 'XF', type: 'Executive Sedan' },
    { name: 'XJ', type: 'Flagship Luxury Sedan' },
    { name: 'F-TYPE', type: 'Performance Sports Car' },
    { name: 'E-PACE', type: 'Compact Performance SUV' },
    { name: 'F-PACE', type: 'Performance SUV' },
    { name: 'I-PACE', type: 'All-Electric SUV' }
  ];

  const commonIssues = [
    {
      title: 'Electrical System Issues',
      description: 'Complex British electrical systems prone to module failures',
      symptoms: ['Dashboard warning lights', 'Infotainment glitches', 'Battery drain', 'Module communication errors'],
      solution: 'SDD/Pathfinder diagnostic scanning and module programming'
    },
    {
      title: 'Cooling System Problems',
      description: 'Dallas heat accelerates coolant system degradation',
      symptoms: ['Overheating warnings', 'Coolant leaks', 'Thermostat failures', 'Water pump issues'],
      solution: 'Complete cooling system service with OEM components'
    },
    {
      title: 'Air Suspension Failures',
      description: 'Common in XJ and F-PACE models',
      symptoms: ['Uneven ride height', 'Compressor noise', 'Warning messages', 'Harsh ride quality'],
      solution: 'Air strut replacement or conversion options available'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Jaguar Repair Dallas | British Luxury Auto Service | Paradise-Performance</title>
        <meta name="description" content="Expert Jaguar repair & service in Dallas, TX. SDD/Pathfinder diagnostics, expert technicians, genuine JLR parts. Specializing in XE, XF, F-PACE, F-TYPE. Call (817) 382-2316." />
        <link rel="canonical" href="https://paradise-performance.com/services/european-car-repair/jaguar" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Jaguar Repair Dallas - Expert British Luxury Car Service" />
        <meta property="og:description" content="Specialized Jaguar repair and maintenance in Dallas. Expert technicians, genuine parts, advanced diagnostics." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://paradise-performance.com/services/european-car-repair/jaguar" />
        
        {/* Schema.org markup */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AutoRepair",
            "name": "Paradise-Performance - Jaguar Repair Dallas",
            "description": "Expert Jaguar repair and service in Dallas, TX. British luxury car specialists.",
            "url": "https://paradise-performance.com/services/european-car-repair/jaguar",
            "telephone": "+18173822316",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "305 Co Rd 3470",
              "addressLocality": "Paradise",
              "addressRegion": "TX",
              "postalCode": "76073"
            },
            "areaServed": ["Dallas", "Fort Worth", "Plano", "Irving", "Arlington"],
            "priceRange": "$$",
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "5.0",
              "reviewCount": "44"
            }
          })}
        </script>

        {/* FAQ Schema for Featured Snippets */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How much does Jaguar maintenance cost?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Jaguar maintenance costs average $1,100 to $1,600 per year depending on the model. An oil change runs $130-$180 at an independent shop (vs $200-$350 at dealerships). Brake service runs $400-$900 for pads and rotors. At Paradise-Performance near Dallas, we're typically 30-40% less than Jaguar dealership prices."
                }
              },
              {
                "@type": "Question",
                "name": "Are Jaguars expensive to maintain?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Jaguars cost more to maintain than mainstream brands, averaging $1,100-$1,600 annually. However, they're less expensive than Land Rover and comparable to BMW/Mercedes. Major repairs like air suspension ($1,500-$3,500) and electrical issues can be costly. Using an independent JLR specialist saves 30-40% vs dealership prices."
                }
              },
              {
                "@type": "Question",
                "name": "What are common Jaguar problems?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Common Jaguar issues include: electrical system problems (module failures, battery drain, infotainment glitches), cooling system failures (water pump, thermostat), air suspension problems (XJ, F-PACE), timing chain tensioner issues (older V8 models), and supercharger maintenance (F-TYPE, XFR). Dallas heat accelerates cooling system wear."
                }
              },
              {
                "@type": "Question",
                "name": "How often should I service my Jaguar?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Jaguars should be serviced every 15,000 miles or 12 months—whichever comes first. Key intervals: oil change every 15,000 miles (we recommend 10,000 in Texas heat), brake fluid every 2 years, coolant every 5 years, and transmission fluid every 60,000 miles. Annual inspections catch issues before they become expensive repairs."
                }
              },
              {
                "@type": "Question",
                "name": "Is the Jaguar F-TYPE reliable?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "The Jaguar F-TYPE is reasonably reliable when properly maintained. The supercharged V6 and V8 engines are robust. Common issues include water pump failures, coolant leaks, and occasional electrical gremlins. The ZF 8-speed transmission is excellent. Regular maintenance with the correct oil spec and coolant prevents most problems."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      <VehicleServiceHero
        brand="Jaguar"
        brandColor="#2E8B57"
        tagline="British Luxury Deserves Expert Care"
        description="Your Jaguar combines British elegance with advanced engineering. Our expert technicians provide the specialized service your luxury vehicle deserves—at 30-40% less than Dallas dealerships."
        features={jaguarFeatures}
        heroImage="/assets/assets/images/jag.webp"
        heroImageAlt="Jaguar Service Center Dallas"
        heroVariant="jaguar"
      />

      {/* Main Content */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4">
          {/* Intro Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Dallas Jaguar Specialists | Luxury British Auto Repair</h2>
            <p className="text-lg text-gray-700 mb-4">
              Jaguar vehicles represent the perfect fusion of British luxury and cutting-edge performance technology. 
              These sophisticated machines require specialized knowledge that goes beyond typical European auto repair—
              from their unique aluminum architecture to advanced Ingenium engines and complex electronic systems.
            </p>
            <p className="text-lg text-gray-700">
              At Paradise-Performance, our expert technicians have extensive experience with all Jaguar models, 
              utilizing genuine JLR diagnostic equipment and proper service procedures to maintain your vehicle's 
              performance, reliability, and value.
            </p>
          </motion.div>

          {/* Services Grid */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <motion.div {...fadeInUp} className="bg-white p-8 rounded-xl shadow-lg border-t-4 border-green-800">
              <Wrench className="w-12 h-12 text-green-800 mb-4" />
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Jaguar Maintenance</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1" />Scheduled maintenance</li>
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1" />Castrol Edge Professional oil service</li>
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1" />ZF transmission service</li>
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1" />Brake fluid flush (annual)</li>
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1" />Coolant system service</li>
              </ul>
            </motion.div>

            <motion.div {...fadeInUp} transition={{ delay: 0.1 }} className="bg-white p-8 rounded-xl shadow-lg border-t-4 border-green-800">
              <Settings className="w-12 h-12 text-green-800 mb-4" />
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Jaguar Diagnostics</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1" />SDD/Pathfinder system diagnostics</li>
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1" />Module programming & coding</li>
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1" />InControl system troubleshooting</li>
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1" />Electrical fault diagnosis</li>
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1" />Pre-purchase inspections</li>
              </ul>
            </motion.div>

            <motion.div {...fadeInUp} transition={{ delay: 0.2 }} className="bg-white p-8 rounded-xl shadow-lg border-t-4 border-green-800">
              <Shield className="w-12 h-12 text-green-800 mb-4" />
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Jaguar Repairs</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1" />Engine & supercharger service</li>
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1" />ZF 8-speed transmission repair</li>
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1" />Air suspension restoration</li>
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1" />Aluminum body repair</li>
                <li className="flex items-start"><CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1" />AWD system service</li>
              </ul>
            </motion.div>
          </div>

          {/* Image Gallery Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <div className="relative h-96 rounded-lg overflow-hidden shadow-2xl">
              <img 
                src="/assets/assets/images/new/euro-repair-img/cayenne-porshe-repair-DFW.webp"
                alt="Jaguar luxury vehicle service"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-transparent flex items-center">
                <div className="p-8 max-w-lg">
                  <h3 className="text-3xl font-bold text-white mb-2">British Luxury Excellence</h3>
                  <p className="text-white/90">Maintaining the prestige and performance of your Jaguar with expert care</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Common Issues Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold mb-8 text-gray-900">Common Jaguar Issues in Dallas Climate</h2>
            
            <div className="space-y-6">
              {commonIssues.map((issue, index) => (
                <motion.div 
                  key={index}
                  {...fadeInUp}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-green-800"
                >
                  <h3 className="text-xl font-bold mb-3 flex items-center">
                    <AlertTriangle className="w-6 h-6 text-yellow-500 mr-2" />
                    {issue.title}
                  </h3>
                  <p className="text-gray-700 mb-3">{issue.description}</p>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-semibold mb-2 text-gray-800">Warning Signs:</h4>
                      <ul className="space-y-1 text-sm text-gray-600">
                        {issue.symptoms.map((symptom, i) => (
                          <li key={i}>• {symptom}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h4 className="font-semibold mb-2 text-gray-800">Our Solution:</h4>
                      <p className="text-sm text-gray-700">{issue.solution}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Model Expertise */}
          <motion.div {...fadeInUp} className="bg-white rounded-xl p-8 shadow-lg mb-16">
            <h2 className="text-3xl font-bold mb-8 text-gray-900">Complete Jaguar Model Coverage</h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {models.map((model, index) => (
                <motion.div 
                  key={index}
                  {...fadeInUp}
                  transition={{ delay: index * 0.05 }}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-lg font-bold text-green-800">{model.name}</h4>
                    <Car className="w-6 h-6 text-gray-400" />
                  </div>
                  <p className="text-sm text-gray-600">{model.type}</p>
                </motion.div>
              ))}
            </div>

            <div className="mt-8 p-4 bg-blue-50 rounded-lg">
              <p className="text-center text-blue-800">
                <Zap className="inline w-5 h-5 mr-2" />
                <strong>I-PACE Electric Specialists:</strong> professional for high-voltage systems and battery diagnostics
              </p>
            </div>
          </motion.div>

          {/* Comparison Table */}
          <motion.div {...fadeInUp} className="bg-white rounded-xl p-8 shadow-lg mb-16">
            <h2 className="text-3xl font-bold mb-6 text-gray-900">Why Choose Paradise-Performance?</h2>
            
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-green-800 text-white">
                    <th className="p-3 text-left">Service Factor</th>
                    <th className="p-3 text-center">Dallas Jaguar Dealer</th>
                    <th className="p-3 text-center">Generic Shop</th>
                    <th className="p-3 text-center">Paradise-Performance</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="p-3 font-semibold">Genuine JLR Parts</td>
                    <td className="p-3 text-center">✓</td>
                    <td className="p-3 text-center">Limited</td>
                    <td className="p-3 text-center text-green-600 font-bold">✓</td>
                  </tr>
                  <tr className="border-b bg-gray-50">
                    <td className="p-3 font-semibold">SDD/Pathfinder Diagnostics</td>
                    <td className="p-3 text-center">✓</td>
                    <td className="p-3 text-center">✗</td>
                    <td className="p-3 text-center text-green-600 font-bold">✓</td>
                  </tr>
                  <tr className="border-b bg-gray-50">
                    <td className="p-3 font-semibold">Wait Time</td>
                    <td className="p-3 text-center">1-2 weeks</td>
                    <td className="p-3 text-center">Varies</td>
                    <td className="p-3 text-center text-green-600 font-bold">1-2 days</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-3 font-semibold">Warranty</td>
                    <td className="p-3 text-center">Manufacturer</td>
                    <td className="p-3 text-center">30-90 days</td>
                    <td className="p-3 text-center text-green-600 font-bold">12 months/12k miles</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </motion.div>

          {/* CTA Section */}
          <motion.div {...fadeInUp} className="text-center bg-gray-900 text-white rounded-xl p-8">
            <h2 className="text-3xl font-bold mb-4">Experience British Luxury Service Excellence</h2>
            <p className="text-xl mb-6 text-gray-300">
              Your Jaguar deserves specialized care from expert technicians who understand British engineering.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:8173822316" className="inline-block px-8 py-4 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 transition-colors">
                Call (817) 382-2316
              </a>
              <Link to="/contact" className="inline-block px-8 py-4 bg-white text-gray-900 font-bold rounded-lg hover:bg-gray-100 transition-colors">
                Schedule Service
              </Link>
            </div>
            <p className="mt-6 text-sm text-gray-400">
              Serving Dallas, Fort Worth, Plano, Irving, and all surrounding areas
            </p>
          </motion.div>

          {/* Related Services */}
          <div className="mt-12 text-center">
            <p className="text-gray-600">
              We also service: {' '}
              <Link to="/services/european-car-repair/land-rover" className="text-blue-600 hover:underline">Land Rover</Link>, {' '}
              <Link to="/services/european-car-repair/bmw" className="text-blue-600 hover:underline">BMW</Link>, {' '}
              <Link to="/services/european-car-repair/mercedes" className="text-blue-600 hover:underline">Mercedes</Link>, {' '}
              and all <Link to="/services/european-car-repair" className="text-blue-600 hover:underline">European auto repair Dallas</Link> services
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default JaguarService;
